import { useT } from "@/lib/i18n-dict";
import { localeMeta } from "@/lib/seo-head";
import { useTx } from "@/lib/i18n-dict";
import { createFileRoute } from "@tanstack/react-router";
import { Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { IsoIllustration } from "@/components/site/Iso";
import { Section, SectionHeading } from "@/components/site/Section";
import { GhostCta, PrimaryCta } from "@/components/site/Cta";
import { ABOUT as ABOUT_SOURCE } from "@/content/pages";
import { alternateLinks } from "@/lib/i18n";

const TITLE = "About us — factory.online, HQ Dubai Internet City";
const DESCRIPTION =
  "13+ years in manufacturing automation. Meet the team behind factory.online and see how we cut production costs and lead times for manufacturers.";

export const Route = createFileRoute("/{-$locale}/about")({
  staticData: { sitemap: true },
  head: ({ params }) => {
    const __m = localeMeta(params, TITLE, DESCRIPTION);
    return ({
    meta: [
      { title: __m.title },
      { name: "description", content: __m.description },
      { property: "og:title", content: __m.title },
      { property: "og:description", content: __m.description },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: alternateLinks("/about"),
  });
  },
  component: About,
});

function About() {
  const ABOUT = useTx(ABOUT_SOURCE);
  const t = useT();
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "var(--gradient-edge)" }}
        />
        <div className="container-page relative grid items-center gap-12 py-20 md:py-28 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-semibold tracking-[0.2em] text-primary uppercase">
              {ABOUT.intro.eyebrow}
            </p>
            <h1 className="mt-3 font-display text-4xl leading-[1.05] font-bold text-balance-tight sm:text-5xl">
              {ABOUT.intro.title}
            </h1>
            <p className="mt-6 text-base text-muted-foreground md:text-lg">{ABOUT.intro.lead}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <PrimaryCta to="/registration-demo">{t("Book a demo")}</PrimaryCta>
              <GhostCta to="/contacts">{t("Contact us")}</GhostCta>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <IsoIllustration
              name="teamwork"
              alt="Isometric illustration of a manufacturing team and company framework"
            />
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <Section>
        <div className="grid gap-10 lg:grid-cols-2">
          <SectionHeading eyebrow="Our story" title="From shop-floor consulting to cloud software" />
          <Reveal delay={80} className="space-y-4">
            {ABOUT.story.map((paragraph) => (
              <p key={paragraph} className="text-sm text-muted-foreground md:text-base">
                {paragraph}
              </p>
            ))}
          </Reveal>
        </div>
      </Section>

      {/* Stats */}
      <Section className="bg-surface/40">
        <SectionHeading eyebrow="Impact" title="What our experience and software have achieved" />
        <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {ABOUT.stats.map((stat, index) => (
            <Reveal key={stat.label} delay={index * 50} className="bg-background p-8 text-center">
              <p className="font-display text-3xl font-bold text-primary md:text-4xl">
                {stat.value}
              </p>
              <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Values */}
      <Section>
        <SectionHeading eyebrow="What we believe" title="Why we built factory.online" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {ABOUT.values.map((value, index) => (
            <Reveal
              key={value.title}
              delay={index * 50}
              className="rounded-xl border border-border p-6"
            >
              <h3 className="font-display text-base font-semibold">{value.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{value.text}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Points */}
      <Section className="bg-surface/40">
        <div className="grid gap-10 lg:grid-cols-2">
          <SectionHeading eyebrow="Why manufacturers choose us" title="Grow faster with factory.online" />
          <Reveal delay={80}>
            <ul className="grid gap-4">
              {ABOUT.points.map((point) => (
                <li key={point} className="flex gap-3 text-base text-muted-foreground md:text-lg">
                  <Check className="mt-1 size-5 shrink-0 text-primary" aria-hidden />
                  {point}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Reveal className="rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t("Grow faster with factory.online")}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">{t("Book a demo")}</PrimaryCta>
            <GhostCta to="/contacts">{t("Contact us")}</GhostCta>
          </div>
        </Reveal>
      </Section>
    </>
  );
}
